import { Button, Stack, Typography } from '@mui/material';
import { type ReactNode, useState } from 'react';

import { useAnalytics } from '~/analytics';
import { cfiReportsReportRetrieve } from '~/data/generated/endpoints/cfi/cfi';
import { BUTTON_SX } from '~/theme';

type Props = {
  reportId: string;
  /** Render the report under review (consultant preview) instead of the PUBLISHED one. */
  preview?: boolean;
  label?: string;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  startIcon?: ReactNode;
};

/** Opens a CFI report's HTML in a new tab, where the reader can save it as a PDF
 * via the browser print dialog ("Save as PDF" button on the page). The HTML is
 * fetched as an authenticated blob — the report endpoint requires a bearer token,
 * so we can't just navigate to it. Shared by the consultant viewer (preview) and
 * the client read-only view (published) so the open/revoke behaviour lives once. */
export const CfiDownloadButton = ({
  reportId,
  preview = false,
  label = 'View report',
  variant = 'contained',
  size = 'medium',
  startIcon,
}: Props) => {
  const { track } = useAnalytics();
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = async () => {
    setIsBusy(true);
    setError(null);
    // Open the tab synchronously inside the click handler — popup blockers only
    // honour window.open while the user gesture is live, which the later `await`
    // would otherwise consume. We then point it at the blob once fetched. Null
    // opener (rather than the `noopener` flag, which forces a null return and
    // loses the handle) keeps the new tab from reaching back into this page.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const { data: html } = await cfiReportsReportRetrieve(reportId, { preview });
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      if (tab) {
        tab.location.href = url;
        track('cfi_report_opened', {
          report_id: reportId,
          mode: preview ? 'preview' : 'published',
        });
      } else {
        // Popup blocked: nothing to navigate, so surface it rather than failing
        // silently.
        setError('Please allow pop-ups for this site to view the report.');
      }
      // Defer revoke: revoking synchronously can blank the just-opened tab
      // before the browser has finished reading the blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error('CFI open report failed', err);
      if (tab) tab.close();
      setError('Could not open the report. Please try again.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Button
        variant={variant}
        size={size}
        startIcon={startIcon}
        disabled={isBusy}
        onClick={handleOpen}
        sx={BUTTON_SX}
      >
        {label}
      </Button>
      {error ? (
        <Typography variant="caption" color="error.main">
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
};
